import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { checkoutAPIService } from '~/api/checkout';
import FooterPublic from '~/components/organisms/FooterPublic/FooterPublic';
import HeaderPublic from '~/components/organisms/HeaderPublic/HeaderPublic';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Text } from '~/components/ui/text';
import { checkoutKeys } from '~/constants/queryKeys';
// import GPay from '~/assets/icons/gPay.png';
//
// import QueueIcon from '@mui/icons-material/Queue';
// import StorageIcon from '@mui/icons-material/Storage';
// import DownloadingIcon from '@mui/icons-material/Downloading';
// import DataThresholdingIcon from '@mui/icons-material/DataThresholding';
// import PaletteIcon from '@mui/icons-material/Palette';
// import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
// import LanguageIcon from '@mui/icons-material/Language';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import {
  StyledBestChoice,
  StyledContainer,
  StyledCreateQRPoster,
  StyledFAQTitle,
  StyledImage,
  StyledLoaderContainer,
  StyledPlan,
  StyledPlanBuyButton,
  StyledPlansContainer,
  StyledPlanTitle,
  StyledSubtitle,
  StyledTitle,
  StyledWrapper,
} from '~/pages/PlansAndPricesPage/styled';
import { accountSlice } from '~/store/reducers/account/slice';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import { COLORS } from '~/styles/colors';
import { getBillingTranslation } from '~/utils/common';

import circleImg from './assets/pnp-circle.webp';

const FAQ_LIST = [
  {
    summary: 'plansAndPrices:faq.changePlan.summary',
    details: 'plansAndPrices:faq.changePlan.details',
  },
  {
    summary: 'plansAndPrices:faq.offerRefunds.summary',
    details: 'plansAndPrices:faq.offerRefunds.details',
  },
  {
    summary: 'plansAndPrices:faq.fullAccess.summary',
    details: 'plansAndPrices:faq.fullAccess.details',
  },
  {
    summary: 'plansAndPrices:faq.free.summary',
    details: 'plansAndPrices:faq.free.details',
  },
  {
    summary: 'plansAndPrices:faq.paymentMethods.summary',
    details: 'plansAndPrices:faq.paymentMethods.details',
  },
  {
    summary: 'plansAndPrices:faq.cancel.summary',
    details: 'plansAndPrices:faq.cancel.details',
  },
];

export default function PlansAndPricesPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isDesktop = useSelector(getIsDesktop);

  const { data: prices = [], isLoading } = useQuery({
    queryKey: checkoutKeys.prices,
    queryFn: () => checkoutAPIService.getPrices('eur'),
    select: (res) => {
      const data = res?.data[0]?.prices;

      return [data[1], data[2], data[0]];
    },
  });

  // const plans = [
  //   {
  //     icon: <QueueIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title0'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle0'),
  //   },
  //   {
  //     icon: <StorageIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title1'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle1'),
  //   },
  //   {
  //     icon: <DownloadingIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title2'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle2'),
  //   },
  //   {
  //     icon: <DataThresholdingIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title3'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle3'),
  //   },
  //   {
  //     icon: <PaletteIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title4'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle4'),
  //   },
  //   {
  //     icon: <QrCodeScannerIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title5'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle5'),
  //   },
  //   {
  //     icon: <LanguageIcon sx={{ color: COLORS.blue, mb: 1, fontSize: 34 }} />,
  //     title: t('payments:plansAdnServices:plans:title6'),
  //     subTitle: t('payments:plansAdnServices:plans:subTitle6'),
  //   },
  // ];

  const accountActions = accountSlice.actions;

  // const paymentsIcons = [Visa, MasterCard, GPay, PayPal];

  const sortedPrices = isDesktop ? prices : [...prices].sort((a) => (a.recurring?.interval === 'year' ? -1 : 1));

  const handleSelectSubscription = (priceId) => {
    dispatch(accountActions.setLoginPriceValue(priceId));

    navigate(PATH.LOGIN);
  };

  return (
    <>
      <HeaderPublic />
      <StyledWrapper>
        <StyledImage src={circleImg} alt="" isRight />
        <StyledImage src={circleImg} alt="" />
        <StyledContainer className="customContainer">
          <StyledTitle variant={isDesktop ? 'h1.5' : 'h2'}>{t('plansAndPrices:title')}</StyledTitle>
          <StyledSubtitle variant={isDesktop ? 'buttonSmallRegular' : 'subheader'}>
            {t('plansAndPrices:subTitle')}
          </StyledSubtitle>
          <StyledPlansContainer>
            {isLoading ? (
              <StyledLoaderContainer>
                <CircularProgress />
              </StyledLoaderContainer>
            ) : (
              sortedPrices.map((item, index) => {
                const isYear = item.recurring?.interval === 'year';
                const currency = item?.currency === 'eur' ? '€' : '$';

                return (
                  <StyledPlan key={index} $isYear={isYear}>
                    {isYear && <StyledBestChoice variant="inputSmall">{t('payments:bestValue50')}</StyledBestChoice>}
                    <StyledPlanTitle variant={isDesktop ? 't16Regular' : 't16Medium'} $isYear={isYear}>
                      {isYear
                        ? `${t('plansAndPrices:months', { months: 12 })}`
                        : `${item?.recurring?.intervalCount} ${item?.recurring?.interval}`}
                    </StyledPlanTitle>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', marginInline: 'auto', width: 'fit-content' }}>
                      <Text variant="h2">{`${currency}${item?.regularMonthlyPrice}`} /</Text>
                      <Text variant="inputVerySmall"> {t('plansAndPrices:month')}</Text>
                    </Box>

                    <Text
                      variant="t18Medium"
                      sx={{ marginTop: '12px', color: isYear ? COLORS.primary : COLORS.grey400, textAlign: 'center' }}
                    >
                      {getBillingTranslation(item.recurring?.interval, item.recurring?.intervalCount)}
                    </Text>

                    <StyledPlanBuyButton
                      variant={isYear ? 'contained' : 'outlined'}
                      color={isYear ? COLORS.white : COLORS.primary}
                      fullWidth
                      onClick={() => handleSelectSubscription(item?.id)}
                    >
                      {t('plansAndPrices:buy')}
                    </StyledPlanBuyButton>
                  </StyledPlan>
                );
              })
            )}

            {/*<Typography*/}
            {/*  sx={{ fontSize: 14, textAlign: 'center', color: COLORS.black06, mt: 1, mb: 2, fontWeight: 500 }}*/}
            {/*>*/}
            {/*  {t('plansAndPrices:bottomTitle')}*/}
            {/*</Typography>*/}

            {/*<Typography sx={{ fontSize: isMobileResolution ? 16 : 25, textAlign: 'center', mb: 2, mt: 6 }}>*/}
            {/*  {t('plansAndPrices:paymentsTitle')}*/}
            {/*</Typography>*/}
            {/*<Box sx={{ display: 'flex', justifyContent: 'center' }}>*/}
            {/*  {paymentsIcons.map((item, index) => {*/}
            {/*    return (*/}
            {/*      <Box*/}
            {/*        key={index}*/}
            {/*        p={1}*/}
            {/*        sx={{*/}
            {/*          border: `1px solid ${COLORS.grayE0}`,*/}
            {/*          mr: 2,*/}
            {/*          display: 'flex',*/}
            {/*          alignItems: 'center',*/}
            {/*          borderRadius: 1,*/}
            {/*        }}*/}
            {/*      >*/}
            {/*        <img src={item} style={{ width: 50, height: 30, objectFit: 'contain' }} />*/}
            {/*      </Box>*/}
            {/*    );*/}
            {/*  })}*/}
            {/*</Box>*/}

            {/*<Typography sx={{ fontSize: isMobileResolution ? 16 : 25, textAlign: 'center', mb: 2, mt: 6 }}>*/}
            {/*  {t('plansAndPrices:allPlans')}*/}
            {/*</Typography>*/}

            {/*<Grid sx={{ justifyContent: 'center' }} columns={2} container mt={5} mb={10}>*/}
            {/*  {plans.map((item, index) => {*/}
            {/*    return (*/}
            {/*      <Grid key={index} width={250} m={1}>*/}
            {/*        {item?.icon}*/}
            {/*        <Typography fontWeight="bold" mb={2} fontSize={14}>*/}
            {/*          {item?.title}*/}
            {/*        </Typography>*/}
            {/*        <Typography fontSize={14} color={COLORS.black06}>*/}
            {/*          {item?.subTitle}*/}
            {/*        </Typography>*/}
            {/*      </Grid>*/}
            {/*    );*/}
            {/*  })}*/}
            {/*</Grid>*/}
          </StyledPlansContainer>
          <StyledFAQTitle variant={isDesktop ? 'h1.5' : 'h2'}>{t('plansAndPrices:faq.title')}</StyledFAQTitle>
          {FAQ_LIST.map(({ summary, details }) => {
            return <Accordion key={summary} summary={t(summary)} details={t(details)} />;
          })}
          <StyledCreateQRPoster />
        </StyledContainer>
      </StyledWrapper>

      <FooterPublic />
    </>
  );
}
